/**
 * CreateCompanyModal Component
 *
 * Modal form for creating a new company with feature selection.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Building2,
  Globe,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  AlertCircle,
  Users,
  Settings,
  ChevronDown,
  ChevronUp,
  Check,
  Search,
} from 'lucide-react';
import {
  superAdminApi,
  CreateCompanyData,
  SubscriptionTier,
  AvailableFeature,
  FeatureCategory,
  LimitPeriod,
  BatchUpdateFeatureItem,
  EntityType,
  ENTITY_TYPE_FEATURES,
  ENTITY_TYPE_INFO,
} from '../../services/adminApi';
import api from '../../services/api';

// Extended CreateCompanyData with features
interface CreateCompanyDataWithFeatures extends CreateCompanyData {
  features?: BatchUpdateFeatureItem[];
}

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCompanyDataWithFeatures) => Promise<void>;
}

interface FeatureSelection {
  enabled: boolean;
  limitValue?: number;
  limitPeriod?: LimitPeriod;
}

const CATEGORY_ICONS: Record<FeatureCategory, string> = {
  core: '🔧',
  advanced: '⚡',
  export: '📤',
  monitoring: '📡',
  api: '🔌',
};

const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  core: 'Core Features',
  advanced: 'Advanced Features',
  export: 'Export Features',
  monitoring: 'Monitoring',
  api: 'API Access',
};

const CATEGORY_ORDER: FeatureCategory[] = ['core', 'advanced', 'export', 'monitoring', 'api'];

export const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<CreateCompanyData>({
    name: '',
    domain: '',
    subscriptionTier: 'free',
    maxUsers: 5,
    status: 'active',
    billingEmail: '',
    contactPhone: '',
    address: '',
    entity_type: 'carrier',
    entity_metadata: {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Feature selection state
  const [availableFeatures, setAvailableFeatures] = useState<AvailableFeature[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featureSelections, setFeatureSelections] = useState<Record<string, FeatureSelection>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<FeatureCategory>>(new Set(['core']));
  const [showFeatures, setShowFeatures] = useState(false);

  // Phase E: DOT search for carrier companies
  const [dotSearchQuery, setDotSearchQuery] = useState<string>('');
  const [dotSearchResults, setDotSearchResults] = useState<any[]>([]);
  const [dotSearching, setDotSearching] = useState(false);
  const [selectedDot, setSelectedDot] = useState<{dotNumber: number, legalName: string} | null>(null);

  // Fetch available features
  useEffect(() => {
    if (isOpen) {
      fetchAvailableFeatures();
    }
  }, [isOpen]);

  const fetchAvailableFeatures = async () => {
    setFeaturesLoading(true);
    try {
      const features = await superAdminApi.getAvailableFeatures();
      setAvailableFeatures(features);

      // Initialize feature selections with defaults
      const initialSelections: Record<string, FeatureSelection> = {};
      features.forEach((feature) => {
        initialSelections[feature.key] = {
          enabled: feature.is_default,
          limitValue: feature.default_limit_value,
          limitPeriod: feature.default_limit_period,
        };
      });
      setFeatureSelections(initialSelections);
    } catch (error) {
      console.error('Failed to fetch features:', error);
    } finally {
      setFeaturesLoading(false);
    }
  };

  // Group features by category
  const featuresByCategory = useMemo(() => {
    const grouped: Record<FeatureCategory, AvailableFeature[]> = {
      core: [],
      advanced: [],
      export: [],
      monitoring: [],
      api: [],
    };

    availableFeatures.forEach((feature) => {
      if (grouped[feature.category]) {
        grouped[feature.category].push(feature);
      }
    });

    return grouped;
  }, [availableFeatures]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        domain: '',
        subscriptionTier: 'free',
        maxUsers: 5,
        status: 'active',
        billingEmail: '',
        contactPhone: '',
        address: '',
        entity_type: 'carrier',
        entity_metadata: {},
      });
      setErrors({});
      setSubmitError(null);
      setShowFeatures(false);
      setExpandedCategories(new Set(['core']));
    }
  }, [isOpen]);

  // Auto-configure features when entity_type changes
  useEffect(() => {
    if (formData.entity_type && availableFeatures.length > 0) {
      const entityFeatures = ENTITY_TYPE_FEATURES[formData.entity_type];
      const newFeatureSelections: Record<string, FeatureSelection> = {};

      availableFeatures.forEach((feature) => {
        newFeatureSelections[feature.key] = {
          enabled: entityFeatures.includes(feature.key),
          limitValue: feature.default_limit_value,
          limitPeriod: feature.default_limit_period,
        };
      });

      setFeatureSelections(newFeatureSelections);
      setShowFeatures(true);  // Auto-expand features section
    }
  }, [formData.entity_type, availableFeatures]);

  // Default max users based on tier
  const tierMaxUsers: Record<SubscriptionTier, number> = {
    free: 5,
    basic: 10,
    pro: 50,
    enterprise: 500,
  };

  // Handle DOT search for carrier companies
  const handleDotSearch = async (dotNumber: string) => {
    setDotSearching(true);
    setDotSearchResults([]);
    try {
      // Search for carrier by DOT number using authenticated API service
      const response = await api.get(`/carriers/search?q=${dotNumber}&limit=10`);

      if (response.data.success && response.data.data) {
        setDotSearchResults(response.data.data);
      }
    } catch (error) {
      console.error('DOT search failed:', error);
      setDotSearchResults([]);
    } finally {
      setDotSearching(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) {
      newErrors.name = 'Company name is required';
    }

    if (formData.domain && !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/.test(formData.domain)) {
      newErrors.domain = 'Invalid domain format';
    }

    if (formData.billingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billingEmail)) {
      newErrors.billingEmail = 'Invalid email format';
    }

    if (formData.maxUsers < 1) {
      newErrors.maxUsers = 'Max users must be at least 1';
    }

    // Phase E: Carrier companies must have a DOT number selected
    if (formData.entity_type === 'carrier' && !selectedDot) {
      newErrors.entity_metadata = 'You must select a DOT number for carrier companies';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Build features array from selections
      const features: BatchUpdateFeatureItem[] = Object.entries(featureSelections)
        .filter(([, selection]) => selection.enabled)
        .map(([featureKey, selection]) => ({
          feature_key: featureKey,
          is_enabled: true,
          limit_value: selection.limitValue,
          limit_period: selection.limitPeriod,
        }));

      const dataWithFeatures: CreateCompanyDataWithFeatures = {
        ...formData,
        features,
      };

      await onSubmit(dataWithFeatures);
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: name === 'maxUsers' ? parseInt(value, 10) || 0 : value,
      };

      // Auto-adjust max users when tier changes
      if (name === 'subscriptionTier') {
        newData.maxUsers = tierMaxUsers[value as SubscriptionTier];
      }

      return newData;
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const toggleFeature = (featureKey: string) => {
    setFeatureSelections((prev) => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        enabled: !prev[featureKey]?.enabled,
      },
    }));
  };

  const updateFeatureLimit = (featureKey: string, field: 'limitValue' | 'limitPeriod', value: number | LimitPeriod) => {
    setFeatureSelections((prev) => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [field]: value,
      },
    }));
  };

  const toggleCategory = (category: FeatureCategory) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const selectAllInCategory = (category: FeatureCategory, select: boolean) => {
    const categoryFeatures = featuresByCategory[category];
    setFeatureSelections((prev) => {
      const newSelections = { ...prev };
      categoryFeatures.forEach((feature) => {
        newSelections[feature.key] = {
          ...newSelections[feature.key],
          enabled: select,
        };
      });
      return newSelections;
    });
  };

  const getSelectedCount = (category: FeatureCategory): number => {
    return featuresByCategory[category].filter(
      (f) => featureSelections[f.key]?.enabled
    ).length;
  };

  const getTotalSelectedCount = (): number => {
    return Object.values(featureSelections).filter((s) => s.enabled).length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Create New Company
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Entity Type Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Company Type <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Select the type of company to automatically configure access and features
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.values(ENTITY_TYPE_INFO).map((info) => (
                    <button
                      key={info.type}
                      type="button"
                      onClick={() => setFormData({ ...formData, entity_type: info.type })}
                      className={`
                        relative p-4 rounded-lg border-2 transition-all duration-200
                        ${formData.entity_type === info.type
                          ? `${info.borderColor} ${info.bgColor} shadow-md`
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }
                      `}
                    >
                      {/* Selected checkmark */}
                      {formData.entity_type === info.type && (
                        <div className="absolute top-2 right-2">
                          <div className={`w-5 h-5 rounded-full ${info.bgColor} ${info.borderColor} border-2 flex items-center justify-center`}>
                            <Check className={`w-3 h-3 ${info.textColor}`} />
                          </div>
                        </div>
                      )}

                      {/* Icon */}
                      <div className="text-3xl mb-2">{info.icon}</div>

                      {/* Label */}
                      <div className={`text-sm font-semibold mb-1 ${
                        formData.entity_type === info.type ? info.textColor : 'text-gray-700'
                      }`}>
                        {info.label}
                      </div>

                      {/* Description */}
                      <div className="text-xs text-gray-600 leading-tight">
                        {info.description}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Features count indicator */}
                {formData.entity_type && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>{ENTITY_TYPE_FEATURES[formData.entity_type].length} features</strong> will be enabled for this {ENTITY_TYPE_INFO[formData.entity_type].label.toLowerCase()}
                    </p>
                  </div>
                )}
              </div>

              {/* DOT Search for Carrier Companies */}
              {formData.entity_type === 'carrier' && (
                <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <span className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-orange-600" />
                      Search for Carrier DOT Number <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Search and select the DOT number for this carrier company. All users will have access to this DOT only.
                  </p>

                  {/* DOT Search Input */}
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={dotSearchQuery}
                      onChange={(e) => {
                        setDotSearchQuery(e.target.value);
                        // Auto-search when user types DOT number (6-8 digits)
                        if (/^\d{6,8}$/.test(e.target.value)) {
                          handleDotSearch(e.target.value);
                        }
                      }}
                      placeholder="Enter DOT number (e.g., 965044)"
                      className="w-full pl-10 pr-4 py-2 border border-orange-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>

                  {/* Selected DOT Display */}
                  {selectedDot && (
                    <div className="bg-white border border-orange-300 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-semibold text-gray-900">DOT #{selectedDot.dotNumber}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{selectedDot.legalName}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDot(null);
                            setFormData({
                              ...formData,
                              entity_metadata: {},
                            });
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Search Results */}
                  {dotSearching && (
                    <div className="text-xs text-gray-500">Searching...</div>
                  )}

                  {dotSearchResults.length > 0 && !selectedDot && (
                    <div className="mt-2 border border-orange-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                      {dotSearchResults.map((result) => (
                        <button
                          key={result.dotNumber}
                          type="button"
                          onClick={() => {
                            setSelectedDot({
                              dotNumber: result.dotNumber,
                              legalName: result.legalName,
                            });
                            setFormData({
                              ...formData,
                              entity_metadata: {
                                allowed_dot_number: result.dotNumber,
                              },
                            });
                            setDotSearchResults([]);
                            setDotSearchQuery('');
                          }}
                          className="w-full text-left p-3 hover:bg-orange-50 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900">DOT #{result.dotNumber}</div>
                          <div className="text-xs text-gray-600">{result.legalName}</div>
                          <div className="text-xs text-gray-500 mt-1">{result.physicalCity}, {result.physicalState}</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {!selectedDot && (
                    <p className="mt-2 text-xs text-orange-600">
                      ⚠️ You must select a DOT number for carrier companies
                    </p>
                  )}
                </div>
              )}

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Acme Corporation"
                    className={`
                      w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                      ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="domain"
                    value={formData.domain}
                    onChange={handleChange}
                    placeholder="acme.com"
                    className={`
                      w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                      ${errors.domain ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  />
                </div>
                {errors.domain && (
                  <p className="mt-1 text-xs text-red-500">{errors.domain}</p>
                )}
              </div>

              {/* Subscription Tier & Max Users */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Tier
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      name="subscriptionTier"
                      value={formData.subscriptionTier}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Users
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      name="maxUsers"
                      value={formData.maxUsers}
                      onChange={handleChange}
                      min="1"
                      className={`
                        w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                        ${errors.maxUsers ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                      `}
                    />
                  </div>
                  {errors.maxUsers && (
                    <p className="mt-1 text-xs text-red-500">{errors.maxUsers}</p>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Billing Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="billingEmail"
                    value={formData.billingEmail}
                    onChange={handleChange}
                    placeholder="billing@acme.com"
                    className={`
                      w-full pl-10 pr-4 py-2 border rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                      ${errors.billingEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                    `}
                  />
                </div>
                {errors.billingEmail && (
                  <p className="mt-1 text-xs text-red-500">{errors.billingEmail}</p>
                )}
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Business Ave, Suite 100&#10;City, State 12345"
                    rows={2}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Feature Access Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => setShowFeatures(!showFeatures)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-gray-900">Feature Access</span>
                    <span className="text-sm text-gray-500">
                      ({getTotalSelectedCount()}/{availableFeatures.length} enabled)
                    </span>
                  </div>
                  {showFeatures ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {showFeatures && (
                  <div className="mt-3 space-y-3">
                    {featuresLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                      </div>
                    ) : (
                      CATEGORY_ORDER.map((category) => {
                        const categoryFeatures = featuresByCategory[category];
                        if (categoryFeatures.length === 0) return null;

                        const isExpanded = expandedCategories.has(category);
                        const selectedCount = getSelectedCount(category);
                        const totalCount = categoryFeatures.length;

                        return (
                          <div
                            key={category}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            {/* Category Header */}
                            <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                              <button
                                type="button"
                                onClick={() => toggleCategory(category)}
                                className="flex items-center gap-2 flex-1"
                              >
                                <span className="text-lg">{CATEGORY_ICONS[category]}</span>
                                <span className="font-medium text-gray-900 text-sm">
                                  {CATEGORY_LABELS[category]}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({selectedCount}/{totalCount})
                                </span>
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400 ml-auto" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
                                )}
                              </button>
                              <div className="flex items-center gap-1 ml-2">
                                <button
                                  type="button"
                                  onClick={() => selectAllInCategory(category, true)}
                                  className="text-xs text-purple-600 hover:text-purple-700 px-2 py-1"
                                >
                                  All
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                  type="button"
                                  onClick={() => selectAllInCategory(category, false)}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                                >
                                  None
                                </button>
                              </div>
                            </div>

                            {/* Category Features */}
                            {isExpanded && (
                              <div className="divide-y divide-gray-100">
                                {categoryFeatures.map((feature) => {
                                  const selection = featureSelections[feature.key] || { enabled: false };
                                  return (
                                    <div
                                      key={feature.key}
                                      className="px-3 py-2 hover:bg-gray-50"
                                    >
                                      <div className="flex items-start gap-3">
                                        <button
                                          type="button"
                                          onClick={() => toggleFeature(feature.key)}
                                          className={`
                                            flex-shrink-0 w-5 h-5 rounded border-2 mt-0.5
                                            flex items-center justify-center transition-colors
                                            ${selection.enabled
                                              ? 'bg-purple-600 border-purple-600'
                                              : 'border-gray-300 hover:border-purple-400'
                                            }
                                          `}
                                        >
                                          {selection.enabled && (
                                            <Check className="w-3 h-3 text-white" />
                                          )}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">
                                              {feature.name}
                                            </span>
                                            {feature.is_default && (
                                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                                Default
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-gray-500 mt-0.5">
                                            {feature.description}
                                          </p>

                                          {/* Limit Configuration */}
                                          {feature.has_limit && selection.enabled && (
                                            <div className="flex items-center gap-2 mt-2">
                                              <input
                                                type="number"
                                                value={selection.limitValue || ''}
                                                onChange={(e) =>
                                                  updateFeatureLimit(
                                                    feature.key,
                                                    'limitValue',
                                                    parseInt(e.target.value, 10) || 0
                                                  )
                                                }
                                                placeholder="Limit"
                                                min="1"
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                              />
                                              <select
                                                value={selection.limitPeriod || 'monthly'}
                                                onChange={(e) =>
                                                  updateFeatureLimit(
                                                    feature.key,
                                                    'limitPeriod',
                                                    e.target.value as LimitPeriod
                                                  )
                                                }
                                                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
                                              >
                                                <option value="daily">per day</option>
                                                <option value="weekly">per week</option>
                                                <option value="monthly">per month</option>
                                                <option value="yearly">per year</option>
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Company'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCompanyModal;
